import classnames from "classnames";
import React from "react";
import FilterOptionGroup from "web/react/components/filter-option-group/filter-option-group";
import { gettext } from "web/script/modules/django-i18n";
import {
    AllSelectedFilterInfo,
    SelectedFilterInfo,
    StandardFilterProps,
} from "web/types/feed-filters";
import {
    CategoryFilterOption,
    InputFilterOption,
    isFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import CategoryFilterItem from "web/react/components/category-filter-item/category-filter-item";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { FeedsFiltersPairOfSelects } from "web/react/components/feeds-filters/feeds-filters-pair-of-selects/feeds-filters-pair-of-selects";
import { PaginatedItem, PaginationExpansionState, PaginationProps } from "./pagination-state";
import ChevronFilterItem from "./chevron-filter-item";
import styles from "./filter-pagination.css";

interface PaginationItemProps extends StandardFilterProps, PaginationProps {
    item: PaginatedItem;
    selectedFilterInfo: SelectedFilterInfo;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
}

function PaginationItemChildren({
    item,
    onPageChange,
    paginationState,
    ...props
}: PaginationItemProps): JSX.Element {
    let children: (CategoryFilterOption | InputFilterOption)[];
    if (item.type == "category") {
        children = item.children;
    } else {
        if (item.type == "pair_of_selects") {
            return <FeedsFiltersPairOfSelects section={item} {...props} />;
        }
        let optionGroup = item.option_groups[0];
        if (optionGroup.type == "filtered_scroll_area") {
            return (
                <FilterOptionGroup
                    isVisible={
                        paginationState.getExpansionState(item) ==
                        PaginationExpansionState.ExpandedActive
                    }
                    {...{ optionGroup, ...props }}
                />
            );
        }
        children = optionGroup.options;
    }

    let reactChildren = children.map((item) => {
        let key = item.name + "=" + item.value.toString();
        if (item.type == "category" && item.name == "category") {
            return <CategoryFilterItem {...{ item, key, ...props }} />;
        } else if (item.type == "category") {
            return (
                <PaginationItem
                    {...{
                        item,
                        key,
                        onPageChange,
                        paginationState,
                        ...props,
                    }}
                />
            );
        } else {
            return (
                <li key={key}>
                    <CheckboxFilterItem {...{ item, ...props }} />
                </li>
            );
        }
    });

    let hasChildren = children.some(
        (item) =>
            item.type == "category" &&
            item.children.length > 0 &&
            props.pendingFilterInfo.getChecked(item)
    );
    return (
        <ol className={classnames(styles.children, { [styles.subChildOpen]: hasChildren })}>
            {reactChildren}
        </ol>
    );
}

function PaginationItem({
    item,
    onPageChange,
    paginationState,
    ...props
}: PaginationItemProps): JSX.Element {
    let expanded = paginationState.getExpansionState(item);
    let isSection = isFilterSection(item);

    function getSubText(): string | undefined {
        if (!props.selectedFilterInfo) {
            return;
        }
        if (item.type == "pair_of_selects") {
            let crumb = props.selectedFilterInfo.breadcrumbs[0];
            if (crumb) {
                return `- ${crumb.label}`;
            }
        } else if (isSection) {
            return props.selectedFilterInfo.breadcrumbs.map((item) => `- ${item.label}`).join(" ");
        } else if (item.type == "category") {
            let count = props.selectedFilterInfo.counts[item.name + "=" + item.value];
            if (count) {
                return `- ${gettext("feed.filters.mobile_selected_count", {
                    count: count,
                })}`;
                // selected needs translation
            }
        }
    }

    return (
        <li
            className={classnames(styles.paginationItem, {
                [styles.sectionItem]: isSection,
            })}
        >
            <ChevronFilterItem
                item={item}
                large={isSection}
                onClick={onPageChange}
                expanded={expanded != PaginationExpansionState.Collapsed}
                subText={getSubText()}
            />
            <div
                className={classnames(styles.expandable, {
                    [styles.expandedActive]: expanded == PaginationExpansionState.ExpandedActive,
                    [styles.expandedInactive]:
                        expanded == PaginationExpansionState.ExpandedInactive,
                    [styles.collapsed]: expanded == PaginationExpansionState.Collapsed,
                })}
            >
                <PaginationItemChildren {...{ item, onPageChange, paginationState, ...props }} />
            </div>
        </li>
    );
}

interface FilterPaginationProps extends StandardFilterProps, PaginationProps {
    allSelectedFilterInfo: AllSelectedFilterInfo;
    sections: UniversalFilterSection[];
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    sidebar?: boolean;
    onClose?: (item: PaginatedItem) => void;
}

function FilterPagination({
    allSelectedFilterInfo,
    onPageChange,
    sections,
    ...props
}: FilterPaginationProps): JSX.Element {
    function handlePageChange(item: PaginatedItem): void {
        let { onClick, onClose, pendingFilterInfo, paginationState, sidebar } = props;
        let expanded = paginationState.getExpansionState(item);
        if (onClose && sidebar && expanded != PaginationExpansionState.Collapsed) {
            onClose(item);
            return;
        } else if (item.type == "category" && !pendingFilterInfo.getChecked(item)) {
            onClick(item);
        }
        onPageChange(item);
    }

    let hasExpandedChildren = props.paginationState.hasActivePage();

    return (
        <div
            className={classnames(styles.container, styles.expandable, {
                [styles.expandedActive]: !hasExpandedChildren,
                [styles.expandedInactive]: hasExpandedChildren,
                [styles.sidebar]: props.sidebar,
                [styles.overlay]: !props.sidebar,
            })}
        >
            <ol className={styles.children}>
                {sections.map((section) => (
                    <PaginationItem
                        key={section.id}
                        item={section}
                        onPageChange={handlePageChange}
                        selectedFilterInfo={allSelectedFilterInfo[section.id]}
                        {...props}
                    />
                ))}
            </ol>
        </div>
    );
}

export default FilterPagination;
